import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { beltForXP } from "@/components/BeltSystem";
import { capture } from "@/lib/analytics";
import { toast } from "sonner";
import {
  Compass, Plus, Check, Archive, RotateCcw, Trash2,
  Pencil, X, Sparkles, Clock, Target,
} from "lucide-react";

// Mirror server-side _VALID_FOCUSES — single source of label catalog.
const ALL_FOCUSES = [
  { slug: "sales",        label: "Sales & Negotiation" },
  { slug: "real_estate",  label: "Real Estate Investing" },
  { slug: "business",     label: "Business Ideas" },
  { slug: "automation",   label: "Automation" },
  { slug: "coding",       label: "Coding & App Building" },
  { slug: "learning",     label: "Learning & Research" },
  { slug: "marketing",    label: "Marketing & Social Media" },
  { slug: "finance",      label: "Finance" },
  { slug: "image",        label: "Image & Creative" },
  { slug: "productivity", label: "Productivity" },
];
const LABEL_BY_SLUG = Object.fromEntries(ALL_FOCUSES.map((f) => [f.slug, f.label]));
const MAX_FOCUSES = 3;
const VALID_TPD = [5, 10, 30];

function FocusChip({ slug, onRemove }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-[var(--primary-soft,#EFF4FF)] text-[var(--primary,#2563EB)] px-2.5 py-1 text-xs font-semibold border border-[rgba(37,99,235,0.18)]"
      data-testid={`focus-chip-${slug}`}
    >
      {LABEL_BY_SLUG[slug] || slug}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${LABEL_BY_SLUG[slug] || slug}`}
          className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-[rgba(37,99,235,0.15)]"
        >
          <X size={10} strokeWidth={3} />
        </button>
      )}
    </span>
  );
}

function VaultChip({ slug, disabled, onAdd }) {
  return (
    <button
      type="button"
      onClick={onAdd}
      disabled={disabled}
      data-testid={`vault-chip-${slug}`}
      className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[var(--line-strong)] px-2.5 py-1 text-xs font-medium text-[var(--ink-2)] hover:border-[var(--primary,#2563EB)] hover:text-[var(--primary,#2563EB)] hover:bg-[var(--primary-soft,#EFF4FF)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors min-h-[28px]"
    >
      <Plus size={10} strokeWidth={3} />
      {LABEL_BY_SLUG[slug] || slug}
    </button>
  );
}

/** Inline rename input for journey cards. */
function NameEditor({ initial, onSave, onCancel, testidPrefix }) {
  const [value, setValue] = useState(initial);
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave(value.trim()); }}
      className="flex items-center gap-2 w-full"
    >
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={60}
        className="brut-input flex-1 min-h-[36px] text-sm font-semibold"
        style={{ padding: "0.4rem 0.7rem" }}
        data-testid={`${testidPrefix}-name-input`}
      />
      <button type="submit" className="btn-primary inline-flex items-center min-h-[36px]" style={{ padding: "0.4rem 0.7rem", fontSize: "0.78rem" }} data-testid={`${testidPrefix}-name-save`}>Save</button>
      <button type="button" onClick={onCancel} className="btn-ghost inline-flex items-center min-h-[36px]" style={{ padding: "0.4rem 0.7rem", fontSize: "0.78rem" }}>Cancel</button>
    </form>
  );
}

export default function JourneysPanel() {
  const { user, refresh } = useAuth();
  const [journeys, setJourneys] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // journey_id currently being mutated
  const [editingName, setEditingName] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newFocuses, setNewFocuses] = useState([]);
  const [newTpd, setNewTpd] = useState(10);

  // Editing focuses on the active journey
  const [editFocusesOpen, setEditFocusesOpen] = useState(false);
  const [draftFocuses, setDraftFocuses] = useState([]);
  const [draftTpd, setDraftTpd] = useState(null);

  const refetch = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/journeys");
      setJourneys(r.data?.journeys || []);
      setActiveId(r.data?.active_journey_id || null);
    } catch (e) {
      toast.error("Couldn't load your journeys.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refetch(); }, []);

  const activeJourney = useMemo(
    () => journeys.find((j) => j.journey_id === activeId) || null,
    [journeys, activeId]
  );

  const archivedJourneys = journeys.filter((j) => j.status === "archived");
  const visibleJourneys = journeys.filter((j) => j.status === "active");

  const activate = async (jid) => {
    if (jid === activeId) return;
    setBusy(jid);
    try {
      await apiClient.post(`/journeys/${jid}/activate`);
      capture("journey_switched", { journey_id: jid });
      await refetch();
      await refresh?.();
      toast.success("Journey switched.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't switch journey.");
    } finally {
      setBusy(null);
    }
  };

  const archive = async (jid) => {
    setBusy(jid);
    try {
      await apiClient.post(`/journeys/${jid}/archive`);
      capture("journey_archived", { journey_id: jid });
      await refetch();
      await refresh?.();
      toast.success("Journey archived.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't archive journey.");
    } finally {
      setBusy(null);
    }
  };

  const restore = async (jid) => {
    setBusy(jid);
    try {
      await apiClient.post(`/journeys/${jid}/restore`);
      await refetch();
      toast.success("Journey restored.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't restore journey.");
    } finally {
      setBusy(null);
    }
  };

  const rename = async (jid, name) => {
    if (!name) { setEditingName(null); return; }
    try {
      await apiClient.patch(`/journeys/${jid}/rename`, { name });
      setEditingName(null);
      await refetch();
      toast.success("Renamed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rename.");
    }
  };

  const toggleNewFocus = (slug) => {
    setNewFocuses((cur) => {
      if (cur.includes(slug)) return cur.filter((s) => s !== slug);
      if (cur.length >= MAX_FOCUSES) return cur;
      return [...cur, slug];
    });
  };

  const createJourney = async () => {
    if (newFocuses.length === 0) {
      toast.error("Pick at least one identity badge.");
      return;
    }
    setBusy("__creating__");
    try {
      const r = await apiClient.post("/journeys", {
        name: newName.trim() || undefined,
        focuses: newFocuses,
        time_per_day: newTpd,
      });
      capture("journey_created", {
        journey_id: r.data?.journey?.journey_id,
        focuses: newFocuses,
        time_per_day: newTpd,
      });
      setCreating(false);
      setNewName(""); setNewFocuses([]); setNewTpd(10);
      await refetch();
      toast.success("Journey created. Activate it any time.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create journey.");
    } finally {
      setBusy(null);
    }
  };

  // ── Edit active journey identity ─────────────────────────────────────
  const openEditFocuses = () => {
    setDraftFocuses(activeJourney?.focuses || []);
    setDraftTpd(activeJourney?.time_per_day || 10);
    setEditFocusesOpen(true);
  };
  const toggleDraftFocus = (slug) => {
    setDraftFocuses((cur) => {
      if (cur.includes(slug)) return cur.filter((s) => s !== slug);
      if (cur.length >= MAX_FOCUSES) return cur;
      return [...cur, slug];
    });
  };
  const saveFocuses = async () => {
    if (draftFocuses.length === 0) {
      toast.error("Pick at least one identity badge.");
      return;
    }
    setBusy("__edit__");
    try {
      await apiClient.patch("/profile/preferences", {
        focuses: draftFocuses,
        time_per_day: draftTpd,
      });
      capture("focuses_updated", { focuses: draftFocuses, time_per_day: draftTpd });
      setEditFocusesOpen(false);
      await refetch();
      await refresh?.();
      toast.success("Identity updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save identity.");
    } finally {
      setBusy(null);
    }
  };

  // Vault on the active journey
  const activeVault = activeJourney?.vault_focuses || [];

  return (
    <div className="brut p-7 mt-5" data-testid="journeys-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <span className="kicker inline-flex items-center gap-2">
            <Compass size={12} /> My Journeys
          </span>
          <h3 className="font-display text-2xl sm:text-3xl mt-1.5 leading-tight">
            Develop multiple versions of yourself.
          </h3>
          <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">
            Each journey keeps its own identity, streaks, and belt — independent paths you can switch between any time.
          </p>
        </div>
        {!creating && visibleJourneys.length < 3 && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="btn-primary inline-flex items-center min-h-[44px]"
            style={{ padding: "0.55rem 1rem", fontSize: "0.85rem" }}
            data-testid="journey-create-open"
          >
            <Plus size={14} /> New journey
          </button>
        )}
      </div>

      {/* Create form */}
      {creating && (
        <div className="mt-5 rounded-2xl border-2 border-dashed border-[var(--line-strong)] p-5" data-testid="journey-create-form">
          <div className="text-xs font-bold tracking-[0.18em] uppercase text-[var(--ink-3)]">Create a new journey</div>
          <input
            type="text"
            placeholder="Name this journey (e.g., Real Estate Investor)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            maxLength={60}
            className="brut-input mt-3"
            data-testid="journey-create-name"
          />
          <div className="mt-4">
            <div className="text-xs font-semibold text-[var(--ink-2)] mb-2">
              Pick up to 3 identity badges ({newFocuses.length}/{MAX_FOCUSES})
            </div>
            <div className="flex flex-wrap gap-2">
              {ALL_FOCUSES.map((f) => {
                const sel = newFocuses.includes(f.slug);
                return (
                  <button
                    key={f.slug}
                    type="button"
                    onClick={() => toggleNewFocus(f.slug)}
                    data-testid={`journey-create-focus-${f.slug}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors min-h-[36px] ${sel ? "bg-[var(--primary,#2563EB)] text-white border-[var(--primary,#2563EB)]" : "bg-white text-[var(--ink-2)] border-[var(--line-strong)] hover:border-[var(--primary,#2563EB)]"}`}
                  >
                    {sel && <Check size={11} strokeWidth={3} />} {f.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-4">
            <div className="text-xs font-semibold text-[var(--ink-2)] mb-2">Time per day</div>
            <div className="flex gap-2">
              {VALID_TPD.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setNewTpd(m)}
                  data-testid={`journey-create-tpd-${m}`}
                  className={`inline-flex items-center justify-center min-w-[64px] min-h-[44px] rounded-full px-3 text-sm font-semibold border-2 transition-colors ${newTpd === m ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white text-[var(--ink-2)] border-[var(--line-strong)] hover:border-[var(--ink)]"}`}
                >
                  {m} min
                </button>
              ))}
            </div>
          </div>
          <div className="mt-5 flex gap-2 justify-end">
            <button type="button" onClick={() => { setCreating(false); setNewFocuses([]); setNewName(""); }} className="btn-ghost inline-flex items-center min-h-[44px]" style={{ padding: "0.55rem 1rem", fontSize: "0.85rem" }}>Cancel</button>
            <button type="button" onClick={createJourney} disabled={busy === "__creating__"} className="btn-primary inline-flex items-center min-h-[44px]" style={{ padding: "0.55rem 1.1rem", fontSize: "0.85rem" }} data-testid="journey-create-submit">
              {busy === "__creating__" ? "Creating…" : <><Sparkles size={14} /> Create journey</>}
            </button>
          </div>
        </div>
      )}

      {/* Journey list */}
      {!loading && visibleJourneys.length > 0 && (
        <ul className="mt-6 space-y-3" data-testid="journeys-list">
          {visibleJourneys.map((j) => {
            const isEditing = editingName === j.journey_id;
            const isBusy = busy === j.journey_id;
            return (
              <li
                key={j.journey_id}
                className={`rounded-2xl border p-4 sm:p-5 transition-colors ${j.is_active ? "border-[var(--primary,#2563EB)] bg-[var(--primary-soft,#EFF4FF)]" : "border-[var(--line)] bg-white hover:border-[var(--line-strong)]"}`}
                data-testid={`journey-row-${j.journey_id}`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <NameEditor
                        initial={j.name}
                        onSave={(n) => rename(j.journey_id, n)}
                        onCancel={() => setEditingName(null)}
                        testidPrefix={`journey-${j.journey_id}`}
                      />
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display text-lg leading-tight text-[var(--ink)] font-semibold">{j.name}</span>
                        {j.is_active && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--primary,#2563EB)]">
                            <Check size={11} strokeWidth={3} /> Active
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => setEditingName(j.journey_id)}
                          aria-label="Rename"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-white text-[var(--ink-3)] hover:text-[var(--ink)]"
                          data-testid={`journey-${j.journey_id}-rename`}
                        >
                          <Pencil size={12} />
                        </button>
                      </div>
                    )}
                    {!isEditing && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {(j.focuses || []).map((s) => <FocusChip key={s} slug={s} />)}
                        {(j.focuses || []).length === 0 && (
                          <span className="text-xs text-[var(--ink-3)] italic">No identity selected yet.</span>
                        )}
                      </div>
                    )}
                    {!isEditing && (
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--ink-2)]">
                        <span><strong className="text-[var(--ink)]">{beltForXP(j.xp).current.name}</strong> Belt</span>
                        <span><strong className="text-[var(--ink)]">{j.streak}</strong>-day streak</span>
                        {j.time_per_day && <span><Clock size={11} className="inline mr-1 -mt-0.5" />{j.time_per_day} min/day</span>}
                        {j.goal_belt && <span><Target size={11} className="inline mr-1 -mt-0.5" />Goal: {j.goal_belt} belt</span>}
                      </div>
                    )}
                  </div>
                  {!isEditing && (
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      {!j.is_active && (
                        <button
                          type="button"
                          onClick={() => activate(j.journey_id)}
                          disabled={isBusy}
                          className="btn-primary inline-flex items-center justify-center min-h-[36px] whitespace-nowrap"
                          style={{ padding: "0.35rem 0.85rem", fontSize: "0.78rem" }}
                          data-testid={`journey-${j.journey_id}-activate`}
                        >
                          {isBusy ? "…" : "Activate"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => archive(j.journey_id)}
                        disabled={isBusy}
                        className="btn-ghost inline-flex items-center justify-center min-h-[36px] whitespace-nowrap text-[var(--ink-3)] hover:text-[var(--ink)]"
                        style={{ padding: "0.35rem 0.7rem", fontSize: "0.78rem" }}
                        data-testid={`journey-${j.journey_id}-archive`}
                      >
                        <Archive size={11} /> Archive
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Identity & Path editor — operates on active journey */}
      {activeJourney && (
        <div className="mt-7 pt-6 border-t border-[var(--line)]" data-testid="identity-editor-block">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <span className="kicker inline-flex items-center gap-2"><Target size={12} /> Identity & Path</span>
              <h4 className="font-display text-lg mt-1.5 leading-tight font-semibold">Edit the active journey</h4>
              <p className="text-xs text-[var(--ink-3)] mt-1">Changes apply to <strong className="text-[var(--ink)]">{activeJourney.name}</strong></p>
            </div>
            {!editFocusesOpen && (
              <button
                type="button"
                onClick={openEditFocuses}
                className="btn-ghost inline-flex items-center min-h-[44px]"
                style={{ padding: "0.5rem 0.95rem", fontSize: "0.82rem" }}
                data-testid="identity-editor-open"
              >
                <Pencil size={12} /> Edit identity
              </button>
            )}
          </div>

          {editFocusesOpen ? (
            <div className="mt-4 rounded-2xl border-2 border-dashed border-[var(--line-strong)] p-5">
              <div className="text-xs font-semibold text-[var(--ink-2)] mb-2">
                Active identity ({draftFocuses.length}/{MAX_FOCUSES})
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {ALL_FOCUSES.map((f) => {
                  const sel = draftFocuses.includes(f.slug);
                  return (
                    <button
                      key={f.slug}
                      type="button"
                      onClick={() => toggleDraftFocus(f.slug)}
                      data-testid={`identity-edit-focus-${f.slug}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors min-h-[36px] ${sel ? "bg-[var(--primary,#2563EB)] text-white border-[var(--primary,#2563EB)]" : "bg-white text-[var(--ink-2)] border-[var(--line-strong)] hover:border-[var(--primary,#2563EB)]"}`}
                    >
                      {sel && <Check size={11} strokeWidth={3} />} {f.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-xs font-semibold text-[var(--ink-2)] mb-2">Time per day</div>
              <div className="flex gap-2 mb-4">
                {VALID_TPD.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDraftTpd(m)}
                    data-testid={`identity-edit-tpd-${m}`}
                    className={`inline-flex items-center justify-center min-w-[64px] min-h-[44px] rounded-full px-3 text-sm font-semibold border-2 transition-colors ${draftTpd === m ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white text-[var(--ink-2)] border-[var(--line-strong)] hover:border-[var(--ink)]"}`}
                  >
                    {m} min
                  </button>
                ))}
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setEditFocusesOpen(false)} className="btn-ghost inline-flex items-center min-h-[44px]" style={{ padding: "0.55rem 1rem", fontSize: "0.82rem" }}>Cancel</button>
                <button type="button" onClick={saveFocuses} disabled={busy === "__edit__"} className="btn-primary inline-flex items-center min-h-[44px]" style={{ padding: "0.55rem 1.1rem", fontSize: "0.82rem" }} data-testid="identity-editor-save">
                  {busy === "__edit__" ? "Saving…" : "Save identity"}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Active focuses preview */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {(activeJourney.focuses || []).map((s) => <FocusChip key={s} slug={s} />)}
              </div>

              {/* Identity Vault — quick-swap unselected slugs */}
              {activeVault.length > 0 && (
                <div className="mt-5">
                  <div className="kicker inline-flex items-center gap-2 mb-2"><Sparkles size={11} /> Identity Vault</div>
                  <p className="text-xs text-[var(--ink-3)] mb-2.5 leading-relaxed">Identities you haven't activated yet. Tap "Edit identity" above to swap one in.</p>
                  <div className="flex flex-wrap gap-2" data-testid="identity-vault">
                    {activeVault.map((s) => (
                      <VaultChip key={s} slug={s} disabled />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Archived */}
      {archivedJourneys.length > 0 && (
        <div className="mt-7 pt-6 border-t border-[var(--line)]" data-testid="archived-section">
          <span className="kicker inline-flex items-center gap-2"><Archive size={12} /> Archived</span>
          <ul className="mt-3 space-y-2">
            {archivedJourneys.map((j) => (
              <li key={j.journey_id} className="flex items-center justify-between gap-3 rounded-xl border border-[var(--line)] bg-[var(--bg-2)] px-4 py-2.5" data-testid={`archived-row-${j.journey_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[var(--ink-2)]">{j.name}</div>
                  <div className="text-xs text-[var(--ink-3)] mt-0.5">{beltForXP(j.xp).current.name} Belt · {(j.focuses || []).map((s) => LABEL_BY_SLUG[s]).filter(Boolean).join(", ") || "—"}</div>
                </div>
                <button
                  type="button"
                  onClick={() => restore(j.journey_id)}
                  disabled={busy === j.journey_id}
                  className="btn-ghost inline-flex items-center min-h-[36px] text-[var(--ink-2)] hover:text-[var(--ink)]"
                  style={{ padding: "0.35rem 0.7rem", fontSize: "0.78rem" }}
                  data-testid={`archived-${j.journey_id}-restore`}
                >
                  <RotateCcw size={11} /> Restore
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
